/**
 * Square-canvas resize + iterative WebP compression in the browser.
 *
 * Targets 50–150 KB while keeping visual quality. Falls back to lower
 * quality steps and finally JPEG if the browser refuses to encode WebP.
 *
 * Designed for low-bandwidth users (Madagascar): the heavy work is done
 * on the client so the upload itself stays small (<500 KB hard cap).
 */
const TARGET_SIZE_PX = 400;
const MIN_BYTES = 50 * 1024;          // 50 KB
const MAX_TARGET_BYTES = 150 * 1024;  // 150 KB (sweet spot)
const HARD_CAP_BYTES = 500 * 1024;    // 500 KB (server reject otherwise)

const QUALITY_LADDER = [0.85, 0.78, 0.7, 0.6, 0.5];

const blobFromCanvas = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, quality));

/**
 * Crop a source image (data URL or Blob URL) by `croppedAreaPixels`,
 * resize to 400×400, encode to WebP (or JPEG fallback) within the size band.
 *
 * @returns {Promise<Blob>} optimized image blob (image/webp preferred)
 */
export const cropAndCompressToWebP = (imageSrc, croppedAreaPixels) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = imageSrc;
    image.onerror = () => reject(new Error('Failed to load image'));
    image.onload = async () => {
      const canvas = document.createElement('canvas');
      canvas.width = TARGET_SIZE_PX;
      canvas.height = TARGET_SIZE_PX;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        TARGET_SIZE_PX,
        TARGET_SIZE_PX,
      );

      // Try WebP first with descending quality.
      let bestUnderTarget = null;
      let smallestOverTarget = null;
      for (const q of QUALITY_LADDER) {
        const blob = await blobFromCanvas(canvas, 'image/webp', q);
        if (!blob) break; // browser doesn't encode webp, fall through to jpeg
        if (blob.size <= MAX_TARGET_BYTES) {
          bestUnderTarget = blob; // first hit ≤150 KB wins (highest quality)
          break;
        }
        if (!smallestOverTarget || blob.size < smallestOverTarget.size) {
          smallestOverTarget = blob;
        }
      }

      if (bestUnderTarget && bestUnderTarget.size >= MIN_BYTES) {
        return resolve(bestUnderTarget);
      }
      // Tiny image already <50 KB at top quality — return as-is.
      if (bestUnderTarget) return resolve(bestUnderTarget);

      // No quality step landed under 150 KB. Use the smallest WebP we got
      // if it's still under the hard cap.
      if (smallestOverTarget && smallestOverTarget.size <= HARD_CAP_BYTES) {
        return resolve(smallestOverTarget);
      }

      // WebP unsupported or still too big — try JPEG fallback.
      let bestJpeg = null;
      for (const q of QUALITY_LADDER) {
        const blob = await blobFromCanvas(canvas, 'image/jpeg', q);
        if (!blob) continue;
        if (blob.size <= MAX_TARGET_BYTES) { bestJpeg = blob; break; }
        if (!bestJpeg || blob.size < bestJpeg.size) bestJpeg = blob;
      }
      if (bestJpeg && bestJpeg.size <= HARD_CAP_BYTES) return resolve(bestJpeg);
      reject(new Error('Could not compress image below 500 KB. Try a different photo.'));
    };
  });

// ─── Generic resize + compress for listing photos ─────────────────
//
// Listing photos preserve aspect ratio (max 1200px on the longest side) and
// target 200–400 KB per image to satisfy bandwidth/storage goals.
const LISTING_MAX_DIM = 1200;
const LISTING_MIN_BYTES = 100 * 1024;        // 100 KB (tolerated when image is already tiny)
const LISTING_TARGET_MAX = 400 * 1024;       // 400 KB (preferred ceiling)
const LISTING_HARD_CAP = 800 * 1024;         // 800 KB (last-resort cap before failing)
const LISTING_QUALITY_LADDER = [0.88, 0.82, 0.75, 0.68, 0.6, 0.5];

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode image'));
    img.src = src;
  });

/**
 * Resize a raw image File down to `LISTING_MAX_DIM` on the longest side,
 * preserving aspect ratio, then iteratively encode to WebP (JPEG fallback)
 * targeting 200–400 KB. Returns a `File` with the same `.name` (extension
 * adjusted to the chosen format) so it can be appended to a FormData.
 */
export const resizeAndCompressListingImage = async (file) => {
  // Skip work for tiny files — already optimal.
  if (file.size <= LISTING_TARGET_MAX && /webp|jpe?g/i.test(file.type)) {
    return file;
  }

  const dataUrl = await readFileAsDataURL(file);
  const img = await loadImage(dataUrl);

  // Compute target dimensions preserving aspect ratio.
  let { naturalWidth: w, naturalHeight: h } = img;
  if (w > LISTING_MAX_DIM || h > LISTING_MAX_DIM) {
    if (w >= h) {
      h = Math.round((h * LISTING_MAX_DIM) / w);
      w = LISTING_MAX_DIM;
    } else {
      w = Math.round((w * LISTING_MAX_DIM) / h);
      h = LISTING_MAX_DIM;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, w, h);

  // Try WebP first.
  let bestUnderTarget = null;
  let smallestOverall = null;
  for (const q of LISTING_QUALITY_LADDER) {
    const blob = await blobFromCanvas(canvas, 'image/webp', q);
    if (!blob) break;
    if (blob.size <= LISTING_TARGET_MAX) { bestUnderTarget = blob; break; }
    if (!smallestOverall || blob.size < smallestOverall.size) smallestOverall = blob;
  }

  let chosen = bestUnderTarget || smallestOverall;
  let chosenType = 'image/webp';

  // WebP not supported (encoder returned null) or still over hard cap → JPEG fallback.
  if (!chosen || chosen.size > LISTING_HARD_CAP) {
    let bestJpeg = null;
    for (const q of LISTING_QUALITY_LADDER) {
      const blob = await blobFromCanvas(canvas, 'image/jpeg', q);
      if (!blob) continue;
      if (blob.size <= LISTING_TARGET_MAX) { bestJpeg = blob; break; }
      if (!bestJpeg || blob.size < bestJpeg.size) bestJpeg = blob;
    }
    if (bestJpeg && bestJpeg.size <= LISTING_HARD_CAP) {
      chosen = bestJpeg;
      chosenType = 'image/jpeg';
    }
  }

  if (!chosen) {
    throw new Error('Could not compress image. Try a smaller or simpler photo.');
  }

  const ext = chosenType === 'image/webp' ? 'webp' : 'jpg';
  // Reuse name root so backend metadata stays meaningful.
  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([chosen], `${baseName}.${ext}`, { type: chosenType });
};

// `LISTING_MIN_BYTES` is intentionally exported for tests/UI hints.
export { LISTING_MIN_BYTES };
